import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import addUrlParameters from '../../tools/addUrlParameters';
import { CATEGORY_RAIL_URL_ANALYTICS_PARAMETERS } from '../../tools/constants';
import ListItemDisclosure from '../ListItemDisclosure/ListItemDisclosure';
import SingleAemEspot from '../SingleAemEspot';
import style from './CategoryNavigationList.module.scss';
import getDisplayableGroups from './utils/getDisplayableGroups';

function CategoryNavigationList({ localNavigation, variant = 'leftRail' }) {
  const displayableGroups = getDisplayableGroups(localNavigation.displayTypes);

  const styleClassnamesByVariant = {
    leftRail: {
      h2Classname: 'mfe-rail-navigation__list-heading',
      ulClassname: 'mfe-rail-navigation__list',
      liClassname: 'mfe-rail-navigation__list-item',
      linkClassname: 'mfe-rail-navigation__list-item-link',
      linkCurrentClassname: 'mfe-rail-navigation__list-item-link--current',
      disclosureClassname: 'mfe-rail-navigation__list-item-disclosure',
      subListClassname: 'mfe-rail-navigation__sub-list',
      subListItemClassname: 'mfe-rail-navigation__sub-list-item',
      subListItemLinkClassname: 'mfe-rail-navigation__sub-list-item-link',
      subListItemLinkCurrentClassname: 'mfe-rail-navigation__sub-list-item-link--current',
    },
    toaster: {
      h2Classname: style.headingLabel,
      ulClassname: style.ullist,
      liClassname: style.listitem,
      linkClassname: style.listitemlink,
      linkCurrentClassname: null,
      disclosureClassname: null,
      subListClassname: style.sublist,
      subListItemClassname: style.sublistitem,
      subListItemLinkClassname: style.sublistitemlink,
      subListItemLinkCurrentClassname: style.sublistitemlinkCurrent,
    },
  };

  const styleClassnames = styleClassnamesByVariant[variant] || styleClassnamesByVariant.leftRail;

  const renderNestedGroup = (nestedGroups) => nestedGroups.map((group) => (
    <ul key={group.displayType} className={styleClassnames.subListClassname}>
      {group.categories.map((category) => (
        <li
          key={category.categoryId}
          className={styleClassnames.subListItemClassname}
          data-aui={variant === 'leftRail' ? category.listItemQaAttribute : null}
        >
          <a
            aria-current={category.isCurrent ? 'page' : null}
            className={classNames(
              styleClassnames.subListItemLinkClassname,
              { [styleClassnames.subListItemLinkCurrentClassname]: category.isCurrent },
            )}
            href={addUrlParameters(category.url, CATEGORY_RAIL_URL_ANALYTICS_PARAMETERS)}
          >
            {category.name}
          </a>
        </li>
      ))}
    </ul>
  ));

  return displayableGroups.map((group, groupIndex) => (
    <React.Fragment key={group.displayType}>
      {group.heading?.value
        ? <h2 className={styleClassnames.h2Classname}>{group.heading.value}</h2> : null}

      <ul className={styleClassnames.ulClassname} data-testid="category-rail-navigation-list">
        {group.categories?.map((category, catIndex) => {
          const link = (
            <a
              aria-current={category.isCurrent ? 'page' : null}
              className={classNames(
                styleClassnames.linkClassname,
                { [styleClassnames.linkCurrentClassname]: category.isCurrent },
              )}
              href={addUrlParameters(category.url, CATEGORY_RAIL_URL_ANALYTICS_PARAMETERS)}
            >
              {category.name}
            </a>
          );

          return (
            <li
              key={category.categoryId}
              className={styleClassnames.liClassname}
              data-aui={variant === 'leftRail' ? category.listItemQaAttribute : null}
            >
              {category.displayTypes.length > 0 ? (
                <div className={styleClassnames.disclosureClassname}>
                  <ListItemDisclosure
                    id={`category-rail-navigation-disclosure-${groupIndex}-${catIndex}`}
                    isDefaultExpanded={category.isCurrent}
                    label={link}
                  >
                    {renderNestedGroup(category.displayTypes)}
                  </ListItemDisclosure>
                </div>
              ) : (
                link
              )}
            </li>
          );
        })}

        {group.bottomEspot ? (
          <li className={styleClassnames.liClassname}>
            <SingleAemEspot
              espotId={group.bottomEspot.espotId}
              htmlContent={group.bottomEspot.htmlContent}
              timestamp={group.bottomEspot.timestamp}
            />
          </li>
        ) : null}
      </ul>
    </React.Fragment>
  ));
}

CategoryNavigationList.propTypes = {
  localNavigation: PropTypes.shape({
    displayTypes: PropTypes.arrayOf(
      PropTypes.shape({
        bottomEspot: PropTypes.shape({
          espotId: PropTypes.string,
          htmlContent: PropTypes.string,
          timestamp: PropTypes.string,
        }),
        categories: PropTypes.arrayOf(
          PropTypes.shape({
            categoryId: PropTypes.string,
            isCurrent: PropTypes.bool,
            listItemQaAttribute: PropTypes.string,
            name: PropTypes.string,
            url: PropTypes.string,
          }),
        ),
        displayType: PropTypes.string,
        heading: PropTypes.shape({
          key: PropTypes.string,
          value: PropTypes.string,
        }),
      }),
    ),
    headingLabel: PropTypes.string,
    headingQaAttribute: PropTypes.string,
    headingScreenReaderOnly: PropTypes.bool,
    headingUrl: PropTypes.string,
  }),
  variant: PropTypes.oneOf(['leftRail', 'toaster']),
};

export default CategoryNavigationList;
